/**
 * Standardized Error Handling Utilities for Authentication
 *
 * This module provides consistent error handling patterns across all authentication
 * files to ensure uniform behavior and maintainability.
 */

import { authLogger } from './logger';

/**
 * Standard authentication error types for consistent error handling
 */
export enum AuthErrorType {
  CONFIGURATION = 'CONFIGURATION',
  NETWORK = 'NETWORK',
  AUTHENTICATION = 'AUTHENTICATION',
  TOKEN_REFRESH = 'TOKEN_REFRESH',
  VALIDATION = 'VALIDATION',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Standardized authentication error class
 */
export class AuthError extends Error {
  public readonly type: AuthErrorType;
  public readonly originalError?: unknown;
  public readonly context?: string;

  constructor(
    message: string,
    type: AuthErrorType = AuthErrorType.UNKNOWN,
    originalError?: unknown,
    context?: string,
  ) {
    super(message);
    this.name = 'AuthError';
    this.type = type;
    this.originalError = originalError;
    this.context = context;
  }
}

/**
 * Configuration error for missing or invalid authentication configuration
 *
 * @param message - Error message describing the configuration issue
 * @param context - Optional context about where the error occurred
 * @returns AuthError with CONFIGURATION type
 */
export function createConfigurationError(
  message: string,
  context?: string,
): AuthError {
  return new AuthError(
    message,
    AuthErrorType.CONFIGURATION,
    undefined,
    context,
  );
}

/**
 * Network error for connection or HTTP-related issues
 *
 * @param message - Error message describing the network issue
 * @param originalError - The original error that caused this
 * @param context - Optional context about where the error occurred
 * @returns AuthError with NETWORK type
 */
export function createNetworkError(
  message: string,
  originalError?: unknown,
  context?: string,
): AuthError {
  return new AuthError(message, AuthErrorType.NETWORK, originalError, context);
}

/**
 * Authentication error for login/logout failures
 *
 * @param message - Error message describing the authentication issue
 * @param originalError - The original error that caused this
 * @param context - Optional context about where the error occurred
 * @returns AuthError with AUTHENTICATION type
 */
export function createAuthenticationError(
  message: string,
  originalError?: unknown,
  context?: string,
): AuthError {
  return new AuthError(
    message,
    AuthErrorType.AUTHENTICATION,
    originalError,
    context,
  );
}

/**
 * Token refresh error for token-related failures
 *
 * @param message - Error message describing the token issue
 * @param originalError - The original error that caused this
 * @param context - Optional context about where the error occurred
 * @returns AuthError with TOKEN_REFRESH type
 */
export function createTokenRefreshError(
  message: string,
  originalError?: unknown,
  context?: string,
): AuthError {
  return new AuthError(
    message,
    AuthErrorType.TOKEN_REFRESH,
    originalError,
    context,
  );
}

/**
 * Validation error for input validation failures
 *
 * @param message - Error message describing the validation issue
 * @param context - Optional context about where the error occurred
 * @returns AuthError with VALIDATION type
 */
export function createValidationError(
  message: string,
  context?: string,
): AuthError {
  return new AuthError(message, AuthErrorType.VALIDATION, undefined, context);
}

/**
 * Standardized error handler that logs errors appropriately and throws AuthError
 *
 * @param error - The error to handle
 * @param context - Context about where the error occurred
 * @param type - The type of authentication error
 * @throws AuthError with standardized format
 */
export function handleAuthError(
  error: unknown,
  context: string,
  type: AuthErrorType = AuthErrorType.UNKNOWN,
): never {
  const message = error instanceof Error ? error.message : 'Unknown error';
  const authError = new AuthError(
    `${context}: ${message}`,
    type,
    error,
    context,
  );

  // Log error appropriately based on type
  switch (type) {
    case AuthErrorType.CONFIGURATION:
      authLogger.error(`❌ Configuration Error in ${context}:`, message);
      break;
    case AuthErrorType.NETWORK:
      authLogger.warn(`🌐 Network Error in ${context}:`, message);
      break;
    case AuthErrorType.AUTHENTICATION:
      authLogger.error(`🔐 Authentication Error in ${context}:`, message);
      break;
    case AuthErrorType.TOKEN_REFRESH:
      authLogger.warn(`🔄 Token Refresh Error in ${context}:`, message);
      break;
    case AuthErrorType.VALIDATION:
      authLogger.error(`✅ Validation Error in ${context}:`, message);
      break;
    default:
      authLogger.error(`❓ Unknown Error in ${context}:`, message);
  }

  throw authError;
}

/**
 * Safe error handler that logs errors but returns a default value instead of throwing
 *
 * @param error - The error to handle
 * @param context - Context about where the error occurred
 * @param defaultValue - Default value to return on error
 * @param type - The type of authentication error
 * @returns The default value
 */
export function handleAuthErrorSafely<T>(
  error: unknown,
  context: string,
  defaultValue: T,
  type: AuthErrorType = AuthErrorType.UNKNOWN,
): T {
  const message = error instanceof Error ? error.message : 'Unknown error';

  // Log error appropriately based on type
  switch (type) {
    case AuthErrorType.CONFIGURATION:
      authLogger.error(`❌ Configuration Error in ${context}:`, message);
      break;
    case AuthErrorType.NETWORK:
      authLogger.warn(`🌐 Network Error in ${context}:`, message);
      break;
    case AuthErrorType.AUTHENTICATION:
      authLogger.warn(`🔐 Authentication Error in ${context}:`, message);
      break;
    case AuthErrorType.TOKEN_REFRESH:
      authLogger.dev(`🔄 Token Refresh Error in ${context}:`, message);
      break;
    case AuthErrorType.VALIDATION:
      authLogger.warn(`✅ Validation Error in ${context}:`, message);
      break;
    default:
      authLogger.error(`❓ Unknown Error in ${context}:`, message);
  }

  return defaultValue;
}

/**
 * Utility to check if an error is a specific type of AuthError
 *
 * @param error - The error to check
 * @param type - The AuthErrorType to check for
 * @returns True if the error is an AuthError of the specified type
 */
export function isAuthErrorOfType(
  error: unknown,
  type: AuthErrorType,
): error is AuthError {
  return error instanceof AuthError && error.type === type;
}

/**
 * Utility to extract a user-friendly error message from any error
 *
 * @param error - The error to extract message from
 * @param fallbackMessage - Fallback message if no user-friendly message found
 * @returns User-friendly error message
 */
export function getUserFriendlyErrorMessage(
  error: unknown,
  fallbackMessage: string = 'An unexpected error occurred',
): string {
  if (error instanceof AuthError) {
    return error.message;
  }

  if (error instanceof Error) {
    // Return error message if it's user-friendly (not too technical)
    if (
      error.message.length < 100 &&
      !error.message.includes('stack') &&
      !error.message.includes('TypeError') &&
      !error.message.includes('ReferenceError')
    ) {
      return error.message;
    }
  }

  return fallbackMessage;
}
